import React, { useMemo } from 'react'
import { useIsCameraMoving } from '@sketch-hq/sketch-web-renderer'
import { HoveredLayerOverlay } from './HoveredLayerOverlay'
import { MeasurementLine } from './MeasurementLine'
import { Orientation } from './types'
import {
  useInspectorHoveredElement,
  useInspectorContext,
  useMapPRMarinaRectToCanvasRelativeRect,
} from '../../hooks'
import {
  ElementType,
  SketchElement,
  isLayerElement,
} from '../../../../inspector'

import { SymbolInstanceOverlay } from './SymbolInstanceOverlay/SymbolInstanceOverlay'
import { useMeasurementData } from './useMeasurementData'
import { OverlayGrid } from './OverlayGrid'
import { OverlayLayout } from './OverlayLayout'
import { SelectedElementOverlays } from './SelectedElementOverlays'
import { ElementRelativeContainer } from '../ElementRelativeContainer'

import * as S from './FrameDetailInspectorOverlay.styles'
import {
  GridAndLayoutFrame,
  useFramesWithGridAndLayout,
} from '../../../components/GridsAndLayoutsProvider'

type InspectorProps = {
  canvasContainerRef: React.RefObject<HTMLDivElement>
}

export function FrameDetailInspectorOverlay({
  canvasContainerRef,
}: InspectorProps) {
  const isCameraMoving = useIsCameraMoving()

  const { selectedElement } = useInspectorContext()

  const hoveredElement = useInspectorHoveredElement()

  /**
   * When we select a layer that is inside a symbol instance, we want to also
   * display the symbol layer (in purple) alongside of the selected layer (in red).
   */
  const symbolInstanceData = useMemo(() => {
    if (!selectedElement || !isLayerElement(selectedElement)) {
      return null
    }

    // If the currently selected element is a symbol instance use it,
    // otherwise check if it belongs to a symbol.
    const symbolLayerElement =
      selectedElement.type === ElementType.SymbolInstance
        ? selectedElement
        : selectedElement.parentSymbolLayerElement

    if (symbolLayerElement) {
      return {
        identifier: symbolLayerElement.prMarinaNode.getIdentifier(),
        bounds: symbolLayerElement.prMarinaNode.getAbsoluteLayerBounds(),
        symbolMaster: symbolLayerElement.symbolMaster,
      }
    }

    return null
  }, [selectedElement])

  const hoveredElementBounds = useMemo(() => {
    // Don't show hover rectangle on top of a selected layer that already has a red rectangle.
    const isSelectedElement =
      selectedElement?.identifier === hoveredElement?.identifier

    if (!hoveredElement || isSelectedElement) {
      return null
    }

    return hoveredElement?.prMarinaNode.getAbsoluteLayerBounds()
  }, [hoveredElement, selectedElement?.identifier])

  const framesWithGridAndLayout = useFramesWithGridAndLayout()

  return (
    <S.Container $isHidden={Boolean(isCameraMoving)}>
      {framesWithGridAndLayout.map(frameWithGridAndLayout => {
        return (
          <GridAndLayoutItem
            key={frameWithGridAndLayout.frameId}
            gridAndLayoutFrame={frameWithGridAndLayout}
          />
        )
      })}

      <MeasurementsLines
        selectedElement={selectedElement}
        hoveredElement={hoveredElement}
      />

      {hoveredElementBounds && hoveredElement && (
        <HoveredLayerOverlay
          bounds={hoveredElementBounds}
          element={hoveredElement}
        />
      )}

      <SelectedElementOverlays selectedElement={selectedElement} />

      {symbolInstanceData && (
        <SymbolInstanceOverlay
          bounds={symbolInstanceData.bounds}
          symbolMaster={symbolInstanceData.symbolMaster}
          canvasContainerRef={canvasContainerRef}
        />
      )}
    </S.Container>
  )
}

type GridAndLayoutItemProps = {
  gridAndLayoutFrame: GridAndLayoutFrame
}

function GridAndLayoutItem({ gridAndLayoutFrame }: GridAndLayoutItemProps) {
  const frameElement = gridAndLayoutFrame.frameElement

  const artboardBounds = frameElement.prMarinaNode.getAbsoluteBounds()
  const artboardBoundsRelative =
    useMapPRMarinaRectToCanvasRelativeRect(artboardBounds)
  const gridSettings =
    'gridSettings' in frameElement ? frameElement.gridSettings : undefined
  const layoutSettings =
    'layoutSettings' in frameElement ? frameElement.layoutSettings : undefined

  if (!artboardBoundsRelative || (!gridSettings && !layoutSettings)) {
    return null
  }

  return (
    // Create a container positioned exactly on top of the artboard.
    // The grid and the layout will be drawn inside this container.
    <ElementRelativeContainer element={frameElement}>
      {gridSettings && (
        <OverlayGrid
          isEnabled={gridAndLayoutFrame.isGridEnabled}
          gridSize={gridSettings.size}
          thickLineStep={gridSettings.thickLineStep}
        />
      )}
      {layoutSettings && (
        <OverlayLayout
          isEnabled={gridAndLayoutFrame.isLayoutEnabled}
          layoutSettings={layoutSettings}
          artboardWidth={artboardBoundsRelative.width}
          artboardHeight={artboardBoundsRelative.height}
        />
      )}
    </ElementRelativeContainer>
  )
}

type MeasurementsLinesProps = {
  selectedElement: SketchElement | null
  hoveredElement: SketchElement | null
}
function MeasurementsLines({
  selectedElement,
  hoveredElement,
}: MeasurementsLinesProps) {
  const measurementData = useMeasurementData(selectedElement, hoveredElement)

  return (
    <>
      {measurementData?.horizontalLine && (
        <MeasurementLine
          line={measurementData.horizontalLine}
          orientation={Orientation.Horizontal}
        />
      )}

      {measurementData?.secondHorizontalLine && (
        <MeasurementLine
          line={measurementData.secondHorizontalLine}
          orientation={Orientation.Horizontal}
        />
      )}

      {measurementData?.verticalLine && (
        <MeasurementLine
          line={measurementData.verticalLine}
          orientation={Orientation.Vertical}
        />
      )}

      {measurementData?.secondVerticalLine && (
        <MeasurementLine
          line={measurementData.secondVerticalLine}
          orientation={Orientation.Vertical}
        />
      )}
    </>
  )
}
